import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import { Brightness4, Brightness7 } from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

/**
 * ThemeToggle component provides a user interface control for switching between light and dark themes
 * Features:
 * - Visual indication of current theme state (sun/moon icons)
 * - Accessibility support with ARIA labels and keyboard navigation
 * - Smooth transitions and hover effects
 * - Integration with the application's theme system
 */
const ThemeToggle = ({ className = '', size = 'medium' }) => {
  const { isDarkMode, toggleTheme, theme } = useTheme();

  // Handle click events
  const handleToggle = () => {
    toggleTheme();
  };

  // Handle keyboard navigation (Enter and Space keys)
  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleTheme();
    }
  };

  // Determine icon and tooltip text based on current theme
  const icon = isDarkMode ? <Brightness7 /> : <Brightness4 />;
  const tooltipText = isDarkMode ? 'Switch to light mode' : 'Switch to dark mode';
  const ariaLabel = `Current theme: ${theme}. Click to switch to ${isDarkMode ? 'light' : 'dark'} mode`;

  return (
    <Tooltip title={tooltipText} arrow>
      <IconButton
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={`theme-toggle ${className}`}
        size={size}
        color="inherit"
        aria-label={ariaLabel}
        role="button"
        sx={{
          transition: 'all 0.3s ease',
          borderRadius: '50%',
          '&:hover': {
            backgroundColor: 'var(--theme-toggle-hover)',
            transform: 'scale(1.1)',
          },
          '&:focus': {
            outline: '2px solid currentColor',
            outlineOffset: '2px',
          },
          '&:active': {
            transform: 'scale(0.95)',
          },
          // Icon rotation animation
          '& .MuiSvgIcon-root': {
            transition: 'transform 0.3s ease',
            transform: isDarkMode ? 'rotate(180deg)' : 'rotate(0deg)',
          },
        }}
      >
        {icon}
      </IconButton>
    </Tooltip>
  );
};

export default ThemeToggle;